import React, { useState } from 'react';
import { Check, ArrowRight, Lock, AlertTriangle, MessageSquareText } from 'lucide-react';
import { isValidUSPhone, normalizeUSPhoneToE164 } from '../../../utils/leadCapture';
import {
  claimQuoteCheckReport,
  QUOTE_CHECK_CONSENT_TEXT,
  ROAST_CONSENT_TEXT,
} from '../../../lib/quoteCheck';

// "Text/email me my full report" — the claim card (the golden-ticket lead).
// Migrated from the raw POST /api/v1/leads capture card to
// POST /api/v1/quote-comparison/market/claim: the backend links the analysis
// run to the lead, stamps the ConsentRecord server-side (IP/UA + consent
// text/version), and queues the report email — so the payload here is ONLY
// the ClaimAnalysisDto fields.
//
// TCPA posture (spec §6): the report on the page is NEVER walled — this card
// is strictly optional. smsConsent is a required checkbox with the
// scope-limited consent string; Roast consent is separate, unticked, and
// non-blocking (v1 stamps roastConsentAt only — no file is stored).
//
// COPY CANON: "Service & Warranty fee"; "about 30 minutes"; never "instant".
//
// Mobile-first: single-column stacked full-width inputs at 390px, tap targets
// >= 44px (inputs/buttons min-h 48px), no horizontal overflow.

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const CONSENT_PAGE_URL = '/compare/quote';

// sessionStorage once-guard so `lead_created` fires at most once per lead id
// per tab (the claim endpoint upserts by normalized phone and returns the
// same lead id on resubmits) — same contract as the old capture card.
function pushLeadCreatedOnce(pushDataLayer, { leadId, created }) {
  const params = {
    event_id: leadId || undefined,
    created: Boolean(created),
    page: CONSENT_PAGE_URL,
    source: 'quote-compare',
  };
  try {
    const key = `obq_lead_created_${leadId || 'unknown'}`;
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, '1');
    pushDataLayer('lead_created', params);
  } catch (e) {
    // sessionStorage unavailable (private mode) — still record the conversion.
    pushDataLayer('lead_created', params);
  }
}

const ClaimCard = ({ runId, pushDataLayer }) => {
  const [firstName, setFirstName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [consented, setConsented] = useState(false);
  const [roastConsent, setRoastConsent] = useState(false); // ALWAYS starts unticked
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmit =
    !submitting &&
    Boolean(runId) &&
    firstName.trim().length > 0 &&
    isValidUSPhone(phone) &&
    emailValid &&
    consented;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const phoneE164 = normalizeUSPhoneToE164(phone);
    if (!phoneE164) {
      setError('Enter a valid US phone number.');
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      const result = await claimQuoteCheckReport({
        runId,
        firstName: firstName.trim(),
        email: email.trim(),
        phone: phoneE164,
        roastConsent,
      });
      pushLeadCreatedOnce(pushDataLayer, result);
      setDone(true);
    } catch (err) {
      setError(err?.message || "We couldn't send your report just now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Confirmation state -----
  if (done) {
    return (
      <div className="rounded-2xl border-[1.5px] border-emerald-600/30 bg-emerald-50/60 px-5 py-8 text-center sm:px-8">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-600 text-white">
          <Check size={26} strokeWidth={3} aria-hidden="true" />
        </div>
        <h3 className="text-xl font-extrabold tracking-tight text-[#0a1628]">
          Done — your full report is on the way
        </h3>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-600">
          We'll <strong>text and email you this full checkup</strong> so you have it in hand when
          you talk to the contractor. The report on this page stays yours either way.
        </p>
        <p className="mx-auto mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-500">
          <Lock size={13} aria-hidden="true" />
          Your info stays with OpenbooQ. We never sell or share it.
        </p>
      </div>
    );
  }

  // ----- Form state -----
  return (
    <div className="overflow-hidden rounded-2xl border-[1.5px] border-primary/40 bg-cyan-50/40 px-5 py-7 sm:px-8 sm:py-8">
      <div className="mx-auto max-w-xl">
        <div className="mb-1.5 flex items-center gap-2 text-[#0a6eb8]">
          <MessageSquareText size={18} aria-hidden="true" />
          <span className="text-[11px] font-extrabold uppercase tracking-[0.12em]">
            Optional · The report above is yours either way
          </span>
        </div>
        <h3 className="text-xl font-extrabold tracking-tight text-[#0a1628] sm:text-2xl">
          Text/email me my full report
        </h3>
        <p className="mt-1.5 mb-5 text-sm leading-relaxed text-slate-600">
          We'll send this checkup — the math, the essentials, the questions to ask — to your phone
          and inbox so it's with you at the kitchen table.
        </p>

        {error && (
          <div
            className="mb-4 flex items-start gap-2.5 rounded-xl border border-red-300 bg-red-50 px-4 py-3.5 text-sm leading-relaxed text-red-700"
            role="alert"
          >
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label
                htmlFor="qc-claim-first-name"
                className="mb-1 block text-[12px] font-bold text-[#0a1628]"
              >
                First name
              </label>
              <input
                id="qc-claim-first-name"
                type="text"
                autoComplete="given-name"
                placeholder="Your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="min-h-[48px] w-full rounded-xl border border-slate-300 bg-white px-4 text-[15px] text-[#0a1628] placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
              />
            </div>
            <div>
              <label
                htmlFor="qc-claim-phone"
                className="mb-1 block text-[12px] font-bold text-[#0a1628]"
              >
                Mobile number
              </label>
              <input
                id="qc-claim-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(555) 123-4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="min-h-[48px] w-full rounded-xl border border-slate-300 bg-white px-4 text-[15px] text-[#0a1628] placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
              />
              {phone.trim() && !isValidUSPhone(phone) && (
                <div className="mt-1 text-xs font-semibold text-red-700">
                  Enter a valid US phone number
                </div>
              )}
            </div>
            <div className="sm:col-span-2">
              <label
                htmlFor="qc-claim-email"
                className="mb-1 block text-[12px] font-bold text-[#0a1628]"
              >
                Email
              </label>
              <input
                id="qc-claim-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="min-h-[48px] w-full rounded-xl border border-slate-300 bg-white px-4 text-[15px] text-[#0a1628] placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
              />
              {email.trim() && !emailValid && (
                <div className="mt-1 text-xs font-semibold text-red-700">
                  Enter a valid email address
                </div>
              )}
            </div>
          </div>

          {/* REQUIRED scope-limited TCPA consent (spec §6) — the backend
              stamps the ConsentRecord server-side on claim. */}
          <label
            htmlFor="qc-claim-consent"
            className="mt-4 flex min-h-[44px] cursor-pointer items-start gap-3 rounded-xl border border-slate-300 bg-white px-4 py-3"
          >
            <input
              id="qc-claim-consent"
              type="checkbox"
              checked={consented}
              onChange={(e) => setConsented(e.target.checked)}
              className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-slate-300 text-primary focus:ring-primary"
            />
            <span className="text-[13px] leading-relaxed text-slate-600">
              {QUOTE_CHECK_CONSENT_TEXT}
            </span>
          </label>

          {/* OPTIONAL Quote Roast consent — separate, unticked, non-blocking. */}
          <label
            htmlFor="qc-claim-roast"
            className="mt-2.5 flex min-h-[44px] cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white/70 px-4 py-3"
          >
            <input
              id="qc-claim-roast"
              type="checkbox"
              checked={roastConsent}
              onChange={(e) => setRoastConsent(e.target.checked)}
              className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-slate-300 text-primary focus:ring-primary"
            />
            <span className="text-[13px] leading-relaxed text-slate-500">
              <span className="font-semibold text-slate-600">Optional:</span> {ROAST_CONSENT_TEXT}{' '}
              Doesn't affect your report either way.
            </span>
          </label>

          <button
            type="submit"
            disabled={!canSubmit}
            aria-busy={submitting}
            className="mt-4 inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-[#0a1628] px-7 py-3 text-[15px] font-bold text-white transition-colors duration-200 hover:bg-[#0a6eb8] disabled:cursor-not-allowed disabled:opacity-45"
          >
            {/* QA W1.1: visible in-flight feedback — spinner + label swap
                while the claim POST runs (button is disabled via canSubmit;
                re-enables on error through the finally block). */}
            {submitting && (
              <span
                className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-white/40 border-t-white motion-reduce:animate-none"
                aria-hidden="true"
              />
            )}
            {submitting ? 'Sending…' : 'Text and email my full report'}
            {!submitting && <ArrowRight size={18} aria-hidden="true" />}
          </button>

          <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-500">
            <Lock size={13} aria-hidden="true" />
            Your info stays with OpenbooQ. We never sell or share it.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ClaimCard;
